import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
    Container, Paper, Typography, Box, TextField,
    MenuItem, Button, Divider, CircularProgress
} from '@mui/material';
import ReCAPTCHA from "react-google-recaptcha";
import api from '../api/axiosInstance';
import { useSnackbar } from '../context/SnackbarContext';

const AU_STATES = [
    { value: 'NSW', label: 'NSW' },
    { value: 'VIC', label: 'VIC' },
    { value: 'QLD', label: 'QLD' },
    { value: 'WA', label: 'WA' },
    { value: 'SA', label: 'SA' },
    { value: 'TAS', label: 'TAS' },
    { value: 'ACT', label: 'ACT' },
    { value: 'NT', label: 'NT' },
];
const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

const PublicWWCCForm = () => {
    const { compositeKey } = useParams();
    const showSnackbar = useSnackbar();
    const [isValid, setIsValid] = useState(true);
    const [tenantId, setTenantId] = useState(null);
    const [tenantName, setTenantName] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [captchaToken, setCaptchaToken] = useState(null); // For Google ReCaptcha or similar
    const recaptchaRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [form, setForm] = useState({
        au_state: '',
        first_given_name: '',
        other_given_names: '',
        surname: '',
        birthdate: '',
        wwcc_number: '',
        expiry_date: '',
    });

    useEffect(() => {
        try {
            const decoded = atob(compositeKey);
            const [id, expiryStr, name] = decoded.split('|'); // Decode the 3-part key

            const expiryDate = new Date(expiryStr);
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Reset time for fair date comparison
            // console.log(`decoded key: ${id} ; ${expiryDate} ; ${name}`);

            // Validate if link is in the past
            if (!expiryStr || isNaN(expiryDate.getTime()) || expiryDate < today) {
                setIsValid(false);
            } else {
                setTenantId(id);
                setTenantName(name);
                setIsValid(true);
            }
        } catch (e) {
            setIsValid(false);
        }
    }, [compositeKey]);

    if (!isValid) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
                <Paper sx={{ p: 4, borderRadius: 2, borderTop: '4px solid #d32f2f' }}>
                    <Typography variant="h5" color="error" gutterBottom>
                        Link Expired
                    </Typography>
                    <Typography variant="body1">
                        This registration link is no longer valid. Please contact your coordinator to receive a current link.
                    </Typography>
                </Paper>
            </Container>
        );
    }

    const handleCaptchaChange = (token) => {
        setCaptchaToken(token);
    };

    // Handle blur (when user leaves a field)
    const handleBlur = (field) => {
        setTouched(prev => ({ ...prev, [field]: true }));
        const error = validateField(field, form[field]);
        setErrors(prev => ({ ...prev, [field]: error }));
    };

    // Handle change (re-validate on every keystroke for instant correction)
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));

        // Only update error if the user has already interacted with it
        if (touched[name]) {
            setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
        }
    };

    const handlePublicSave = async () => {
        const allFields = ['au_state', 'surname', 'first_given_name', 'wwcc_number', 'birthdate', 'expiry_date'];
        const newErrors = {};
        const newTouched = {};

        allFields.forEach(f => {
            newErrors[f] = validateField(f, form[f]);
            newTouched[f] = true;
        });

        setErrors(newErrors);
        setTouched(newTouched);

        const hasErrors = Object.values(newErrors).some(err => err !== "");
        if (hasErrors) {
            showSnackbar("Please correct the errors before submitting.", "error");
            return;
        }

        if (!captchaToken) {
            showSnackbar("Please complete the reCAPTCHA.", "error");
            return;
        }

        setLoading(true);

        // Updated payload to use tenantId instead of ceremonyUuid
        const payload = {
            ...form,
            tenant_id: tenantId, // Match your backend expectation
            captchaToken: captchaToken
        };

        try {
            const response = await api.post('/wwcc/public', payload);

            if (response.status === 201) {
                showSnackbar("Success! Your details have been submitted.", "success");
                setSubmitted(true);
            }
        } catch (error) {
            console.error("Submission failed", error);

            // Axios stores the server response in error.response
            const errorMessage = error.response?.data?.error || "Submission failed";
            showSnackbar(errorMessage, "error");

            // Reset captcha on failure so they can try again
            recaptchaRef.current.reset();
            setCaptchaToken(null);
        } finally {
            setLoading(false);
        }
    };

    const validateField = (name, value) => {
        switch (name) {
            case 'au_state':
                return value?.trim().length > 0 ? "" : "Surname is required";
            case 'surname':
                return value?.trim().length > 0 ? "" : "Surname is required";
            case 'first_given_name':
                return value?.trim().length > 0 ? "" : "First name is required";
            case 'wwcc_number':
                // Basic alphanumeric check; you can make this state-specific later
                return /^[a-zA-Z0-9 -]+$/.test(value) ? "" : "Invalid WWCC number format";
            case 'birthdate': {
                const date = new Date(value);
                const year = date.getFullYear();
                if (!value) return "Birth date is required";
                if (year < 1900) return "This make you pretty old! Are you sure?";
                if (date > new Date()) return "Birth date cannot be in the future";
                return "";
            }
            case 'expiry_date': {
                const date = new Date(value);
                const maxFuture = new Date();
                maxFuture.setFullYear(maxFuture.getFullYear() + 10);
                if (!value) return "Expiry date is required";
                if (date < new Date()) return "This WWCC has already expired";
                if (date > maxFuture) return "Expiry cannot be more than 10 years away";
                return "";
            }
            default:
                return "";
        }
    };

    if (submitted) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
                <Paper sx={{ p: 4, borderRadius: 2 }}>
                    <Typography variant="h5" color="success.main" gutterBottom>
                        Submission Successful
                    </Typography>
                    <Typography variant="body1">
                        Thank you! Your Working With Children Check details have been recorded for verification.
                    </Typography>
                </Paper>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper sx={{ p: 4, boxShadow: 8, borderRadius: 1 }}>

                {/* Branded Header */}
                <Box
                    onClick={() => window.location.href = '/'}
                    className="header"
                    sx={{
                        mx: -4,
                        mt: -4,
                        mb: 3,
                        cursor: 'pointer',
                        borderRadius: '8px 8px 0 0',
                        overflow: 'hidden',
                        lineHeight: 0,
                    }}
                >
                    <Box
                        component="img"
                        src="/Verisafe logo light.png"
                        alt="VeriSafe Logo"
                        sx={{
                            width: '400px',
                            height: 'auto',
                            display: 'block',
                        }}
                    />
                </Box>

                <Typography variant="h5" sx={{ mt: 1, mb: 1, fontWeight: 'bold' }}>
                    Volunteer Registration for {tenantName || "your organisation"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, whiteSpace: 'pre-line' }}>
                    {`Thank you for your interest in volunteering as a parent/carer helper.\n\nTo ensure the safety and welbeeing of all children, the government requires all parent/carer helpers to hold a valid Working With Children Check (WWCC) clearance.\n\nThis form collects your details and WWCC information so that we can verify your eligibility to assist at/attend events. Your information will be reviewed by the organiser of events as well as the organisation's designated WWCC verifier.`}
                </Typography>

                <Divider sx={{ mb: 3 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <TextField
                        select
                        label="State of Issue"
                        name="au_state"
                        value={form.au_state}
                        sx={{ width: '200px' }}
                        onChange={handleChange}
                        onBlur={() => handleBlur('au_state')}
                        error={touched.au_state && !!errors.au_state}
                        helperText={touched.au_state && errors.au_state}
                    >
                        {AU_STATES.map((option) => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                    </TextField>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="Surname"
                            name="surname"
                            fullWidth
                            value={form.surname}
                            onChange={handleChange}
                            onBlur={() => handleBlur('surname')}
                            error={touched.surname && !!errors.surname}
                            helperText={touched.surname && errors.surname}
                        />
                        <TextField
                            label="First Given Name"
                            name="first_given_name"
                            fullWidth
                            value={form.first_given_name}
                            onChange={handleChange}
                            onBlur={() => handleBlur('first_given_name')}
                            error={touched.first_given_name && !!errors.first_given_name}
                            helperText={touched.first_given_name && errors.first_given_name}
                        />
                    </Box>

                    <TextField label="Other given (middle) names" fullWidth value={form.other_given_names} onChange={(e) => setForm({ ...form, other_given_names: e.target.value })} />

                    <TextField
                        label="Birthdate"
                        name="birthdate"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={form.birthdate}
                        onChange={handleChange}
                        onBlur={() => handleBlur('birthdate')}
                        error={touched.birthdate && !!errors.birthdate}
                        helperText={touched.birthdate && errors.birthdate}
                    />

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="WWCC Number"
                            name="wwcc_number"
                            fullWidth
                            value={form.wwcc_number}
                            placeholder="WWC1234567E"
                            onChange={handleChange}
                            onBlur={() => handleBlur('wwcc_number')}
                            error={touched.wwcc_number && !!errors.wwcc_number}
                            helperText={touched.wwcc_number && errors.wwcc_number}
                        />

                        <TextField
                            label="Expiry Date"
                            type="date"
                            name="expiry_date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={form.expiry_date}
                            onChange={handleChange}
                            onBlur={() => handleBlur('expiry_date')}
                            error={touched.expiry_date && !!errors.expiry_date}
                            helperText={touched.expiry_date && errors.expiry_date}
                        />
                    </Box>

                    {/* CAPTCHA PLACEHOLDER */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                        <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={RECAPTCHA_SITE_KEY}
                            onChange={handleCaptchaChange}
                        />
                    </Box>

                    <Button
                        variant="contained"
                        size="large"
                        onClick={handlePublicSave}
                        sx={{ mt: 2, py: 1.5, position: 'relative' }} // relative position for the spinner
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : "Submit Verification Details"}
                    </Button>

                    <Typography variant="caption" color="text.secondary" textAlign="center">
                        Your information is encrypted and sent directly to our secure server for official verification.
                    </Typography>
                </Box>
            </Paper>
        </Container>
    );
};

export default PublicWWCCForm;
